import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Reusable "Email this document" dialog for invoices, pro-formas and receipts.
 *
 * Props:
 *  - resource: "invoices" | "proformas" | "receipts"
 *  - id: record id
 *  - defaultTo: email pre-fill (buyer / remitter)
 *  - label: what recipients see in the dialog title (e.g. "invoice", "pro-forma")
 *  - onSent: called with the updated record on success
 *  - triggerLabel: custom trigger button label (optional)
 *  - triggerTestId
 */
export function EmailSendButton({
  resource,
  id,
  defaultTo,
  label = "document",
  onSent,
  triggerLabel = "Email",
  triggerTestId,
  variant = "outline",
  triggerClassName = "",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState(defaultTo || "");
  const [sending, setSending] = useState(false);
  useEffect(() => { setTo(defaultTo || ""); }, [defaultTo, open]);

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/${resource}/${id}/send`, { to });
      toast.success(`${label[0].toUpperCase()}${label.slice(1)} sent to ${to}`);
      setOpen(false);
      onSent?.(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message = typeof detail === "object" ? detail?.message : formatApiError(detail);
      toast.error(message || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant={variant}
        onClick={() => setOpen(true)}
        data-testid={triggerTestId || `send-${resource}-button`}
        className={triggerClassName}
        disabled={disabled}
      >
        <Send className="w-4 h-4 mr-1.5" /> {triggerLabel}
      </Button>
      <Dialog open={open} onOpenChange={(v) => !sending && setOpen(v)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Email {label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Mail className="w-3 h-3" /> The PDF (or receipt summary) is attached automatically. Sends via Resend using your saved sender identity.
            </p>
            <div>
              <Label className="text-xs uppercase">Recipient email</Label>
              <Input
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                data-testid={`${triggerTestId || `send-${resource}`}-input`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={sending}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={!to || sending}
              data-testid={`${triggerTestId || `send-${resource}`}-submit`}
            >
              {sending ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
